<template>
  <div class="modal dark-modal" :class="{ 'is-active': show }">
    <div class="modal-background" @click="close"></div>
    <div class="modal-card two-factor-settings-modal">
      <header class="modal-card-head">
        <p class="modal-card-title">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>
          {{ $t('twoFactor.title') || 'Two-Factor Authentication' }}
        </p>
        <button class="delete" aria-label="close" @click="close"></button>
      </header>
      <section class="modal-card-body">
        <div v-if="loading" class="loading-state">
          <span class="loader"></span>
          <span>{{ $t('common.loading') || 'Loading...' }}</span>
        </div>

        <template v-else>
          <div class="status-section">
            <div class="status-indicator" :class="{ 'is-enabled': status.enabled }">
              <span class="status-dot"></span>
              <span class="status-text">
                {{ status.enabled 
                  ? ($t('twoFactor.statusEnabled') || '2FA is enabled') 
                  : ($t('twoFactor.statusDisabled') || '2FA is not enabled') }}
              </span>
            </div>
            <p v-if="status.enabled && status.enabled_at" class="status-date">
              {{ $t('twoFactor.enabledSince') || 'Enabled since' }}: {{ formatDate(status.enabled_at) }}
            </p>
          </div>

          <div class="actions-section">
            <template v-if="!status.enabled">
              <button type="button" class="button is-primary" @click="showSetupModal = true">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
                {{ $t('twoFactor.enable') || 'Enable 2FA' }}
              </button>
            </template>

            <template v-else>
              <button type="button" class="button is-warning" @click="showDisableModal = true">
                {{ $t('twoFactor.disable') || 'Disable 2FA' }}
              </button>
              <button type="button" class="button" @click="showRegenerateModal = true">
                {{ $t('twoFactor.regenerateRecoveryCodes') || 'Regenerate Recovery Codes' }}
              </button>
            </template>
          </div>
        </template>
      </section>
      <footer class="modal-card-foot">
        <button class="button" @click="close">{{ $t('common.close') || 'Close' }}</button>
      </footer>
    </div>

    <!-- 2FA Setup Modal -->
    <two-factor-setup
      v-if="showSetupModal"
      :visible="showSetupModal"
      @close="showSetupModal = false"
      @success="handleSetupSuccess"
    />

    <!-- Disable 2FA Modal -->
    <div v-if="showDisableModal" class="modal is-active">
      <div class="modal-background" @click="showDisableModal = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t('twoFactor.disableTitle') || 'Disable Two-Factor Authentication' }}</p>
          <button class="delete" @click="showDisableModal = false"></button>
        </header>
        <section class="modal-card-body">
          <p class="warning-text">{{ $t('twoFactor.disableWarning') || 'Disabling 2FA will make your account less secure.' }}</p>
          <div class="field">
            <label class="label">{{ $t('password') || 'Password' }}</label>
            <input v-model="disableForm.password" type="password" class="input" :placeholder="$t('users.placeholders.password') || 'Enter password'" />
          </div>
          <div class="field">
            <label class="label">{{ $t('twoFactor.verificationCode') || 'Verification Code' }}</label>
            <input v-model="disableForm.code" type="text" class="input" maxlength="12" :placeholder="$t('twoFactor.codePlaceholder') || 'Enter code'" />
          </div>
          <p v-if="disableError" class="help is-danger">{{ disableError }}</p>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-danger" :class="{ 'is-loading': disabling }" :disabled="disabling" @click="handleDisable">
            {{ $t('twoFactor.disable') || 'Disable 2FA' }}
          </button>
          <button class="button" @click="showDisableModal = false">{{ $t('cancel') || 'Cancel' }}</button>
        </footer>
      </div>
    </div>

    <!-- Regenerate Recovery Codes Modal -->
    <div v-if="showRegenerateModal" class="modal is-active">
      <div class="modal-background" @click="closeRegenerateModal"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t('twoFactor.regenerateRecoveryCodes') || 'Regenerate Recovery Codes' }}</p>
          <button class="delete" @click="closeRegenerateModal"></button>
        </header>
        <section class="modal-card-body">
          <template v-if="!newRecoveryCodes.length">
            <p>{{ $t('twoFactor.regenerateWarning') || 'This will invalidate your existing recovery codes.' }}</p>
            <div class="field">
              <label class="label">{{ $t('password') || 'Password' }}</label>
              <input v-model="regenerateForm.password" type="password" class="input" />
            </div>
            <div class="field">
              <label class="label">{{ $t('twoFactor.verificationCode') || 'Verification Code' }}</label>
              <input v-model="regenerateForm.code" type="text" class="input" maxlength="6" />
            </div>
            <p v-if="regenerateError" class="help is-danger">{{ regenerateError }}</p>
          </template>
          <template v-else>
            <p class="success-text">{{ $t('twoFactor.saveRecoveryCodes') || 'Save these new recovery codes:' }}</p>
            <div class="recovery-codes">
              <code v-for="(code, i) in newRecoveryCodes" :key="i" class="recovery-code">{{ code }}</code>
            </div>
          </template>
        </section>
        <footer class="modal-card-foot">
          <template v-if="!newRecoveryCodes.length">
            <button class="button is-primary" :class="{ 'is-loading': regenerating }" :disabled="regenerating" @click="handleRegenerate">
              {{ $t('twoFactor.regenerate') || 'Regenerate' }}
            </button>
          </template>
          <button class="button" @click="closeRegenerateModal">{{ newRecoveryCodes.length ? ($t('common.done') || 'Done') : ($t('cancel') || 'Cancel') }}</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import twoFactorApi from '../api/twoFactor'
import { TwoFactorSetup } from './users'

export default {
  name: 'TwoFactorSettingsModal',
  components: { TwoFactorSetup },
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: true,
      status: { enabled: false, verified: false, enabled_at: null },
      showSetupModal: false,
      showDisableModal: false,
      showRegenerateModal: false,
      disableForm: { password: '', code: '' },
      regenerateForm: { password: '', code: '' },
      disabling: false,
      regenerating: false,
      disableError: '',
      regenerateError: '',
      newRecoveryCodes: []
    }
  },
  watch: {
    show(val) {
      if (val) {
        this.loadStatus()
      }
    }
  },
  mounted() {
    if (this.show) {
      this.loadStatus()
    }
  },
  methods: {
    close() {
      this.$emit('update:show', false)
    },
    async loadStatus() {
      this.loading = true
      try {
        const res = await twoFactorApi.getStatus(this.$http)
        this.status = res?.data?.data || res?.data || {}
      } catch (err) {
        console.error('Failed to load 2FA status:', err)
      } finally {
        this.loading = false
      }
    },
    handleSetupSuccess() {
      this.loadStatus()
      this.$store.dispatch('NOTIFY', { type: 'success', text: this.$t('twoFactor.enabled') || '2FA enabled' })
    },
    async handleDisable() {
      this.disabling = true
      this.disableError = ''
      try {
        await twoFactorApi.disable(this.$http, this.disableForm.password, this.disableForm.code)
        this.showDisableModal = false
        this.disableForm = { password: '', code: '' }
        this.loadStatus()
        this.$store.dispatch('NOTIFY', { type: 'success', text: this.$t('twoFactor.disabled') || '2FA disabled' })
      } catch (err) {
        this.disableError = err.response?.data?.message || err.message || 'Failed to disable 2FA'
      } finally {
        this.disabling = false
      }
    },
    async handleRegenerate() {
      this.regenerating = true
      this.regenerateError = ''
      try {
        const res = await twoFactorApi.regenerateRecoveryCodes(this.$http, this.regenerateForm.password, this.regenerateForm.code)
        this.newRecoveryCodes = res?.data?.data?.recovery_codes || res?.data?.recovery_codes || []
      } catch (err) {
        this.regenerateError = err.response?.data?.message || err.message || 'Failed to regenerate codes'
      } finally {
        this.regenerating = false
      }
    },
    closeRegenerateModal() {
      this.showRegenerateModal = false
      this.regenerateForm = { password: '', code: '' }
      this.newRecoveryCodes = []
      this.regenerateError = ''
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString()
    }
  }
}
</script>

<style lang="scss" scoped>
.two-factor-settings-modal {
  max-width: 500px;
  
  .modal-card-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    svg {
      color: #3273dc;
    }
  }
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem;
  color: #aaa;
}

.loader {
  width: 20px;
  height: 20px;
  border: 2px solid #444;
  border-top-color: #3273dc;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.status-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 6px;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.status-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #f14668;
}

.status-indicator.is-enabled .status-dot {
  background: #48c774;
}

.status-text {
  font-weight: 500;
}

.status-date {
  font-size: 0.85rem;
  color: #888;
  margin-top: 0.5rem;
}

.actions-section {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.warning-text {
  color: #f14668;
  margin-bottom: 1rem;
}

.success-text {
  color: #48c774;
  margin-bottom: 1rem;
}

.recovery-codes {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
}

.recovery-code {
  display: block;
  font-family: monospace;
  padding: 0.5rem;
  background: #f5f5f5;
  border-radius: 4px;
  text-align: center;
  color: #333;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
